import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback to help identify the route */
  routeLabel?: string;
}

interface State {
  error: Error | null;
}

/**
 * Route-level error boundary.
 *
 * Catches:
 *   • runtime exceptions thrown during render / effects in any page component
 *   • dynamic-import / chunk-load failures (transient Vite/HMR issues that
 *     would otherwise unmount the tree and leave a blank screen)
 *
 * Renders a minimal, themed fallback with a "Reload" action instead of the
 * empty white page that React produces when an unhandled error escapes.
 *
 * Added 2026-04-21 after a regression where transient module-load failures
 * (AggregateHealthCard.tsx 404, PageGate.tsx 502 during HMR storm) caused
 * /app/overview to render briefly and then blank out for the user.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the browser console so the existing dev/QA tooling can pick it up.
    // We intentionally do not swallow — the boundary only stops the unmount cascade.
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary] Uncaught error in route", {
      route: this.props.routeLabel,
      message: error?.message,
      stack: error?.stack,
      componentStack: info?.componentStack,
    });
  }

  private handleReload = () => {
    // Hard reload bypasses any stale module graph (typical for chunk-load errors)
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isChunkError =
      /chunk|loading css chunk|dynamically imported module|failed to fetch dynamically/i.test(
        this.state.error.message ?? "",
      );

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-warning/10">
            <AlertTriangle className="w-6 h-6 text-warning" />
          </div>
          <h1 className="text-lg font-heading font-bold text-foreground">
            {isChunkError ? "This page failed to load" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isChunkError
              ? "A required module did not load. This is usually a temporary network or deployment issue."
              : "We hit an unexpected error while rendering this page."}
          </p>
          <div className="flex items-center justify-center gap-2 pt-1">
            <Button onClick={this.handleReload} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Reload page
            </Button>
          </div>
          {import.meta.env.DEV && this.state.error?.message && (
            <details className="text-left text-xs text-muted-foreground/70 mt-4">
              <summary className="cursor-pointer">Error details (dev only)</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
